import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import PlanCards, { usePlans } from "@/components/PlanCards";
import { billing, formatErr } from "@/lib/api";
import { CheckCircle, XCircle, CircleNotch, CreditCard } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

export default function Billing() {
  const data = usePlans();
  const [state, setState] = useState(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(null);
  useEffect(() => { billing.me(true).then(setState).catch(() => setFailed(true)); }, []);

  const choose = async (p) => {
    setBusy(p.id);
    try { const r = await billing.checkout(p.lookup_key); window.location.href = r.checkout_url; }
    catch (e) { toast.error(formatErr(e)); setBusy(null); }
  };
  const manage = async () => {
    setBusy("portal");
    try { const r = await billing.portal(); window.location.href = r.url; }
    catch (e) { toast.error(formatErr(e)); setBusy(null); }
  };

  return <AppShell>
    <div className="mb-6">
      <div className="eyebrow text-accent">Your plan</div>
      <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Plan & billing</h1>
    </div>
    {!state && !failed && <div className="text-muted-foreground" data-testid="billing-loading">Loading your plan…</div>}
    {failed && <div className="border-l-4 border-l-destructive bg-white border p-5" data-testid="billing-error">Couldn't load your plan. Check your connection and refresh the page.</div>}
    {state && <StatusCard state={state} onManage={manage} busy={busy === "portal"}/>}
    {data && state?.fleet_role === "driver" && <p className="mt-6 text-muted-foreground" data-testid="billing-driver-note">Your fleet owner manages the plan. Ask them if you need to change it.</p>}
    {data && state && state.status !== "complimentary" && state.fleet_role !== "driver" && <>
      <h2 className="font-display font-bold text-xl mt-8 mb-4">{state?.status === "active" ? "All plans" : "Choose a plan"}</h2>
      <PlanCards plans={data.plans} currentId={state?.status === "active" ? state.plan?.id : null} action={(p, current) =>
        current ? <div className="eyebrow text-accent text-center py-3" data-testid={`plan-current-${p.id}`}>Your current plan</div>
        : state?.status === "active" ? <button onClick={manage} className="btn-dark w-full" data-testid={`plan-switch-${p.id}`}>Switch to this plan</button>
        : <button onClick={() => choose(p)} disabled={!!busy} data-testid={`plan-choose-${p.id}`} className="btn-amber w-full">
            {busy === p.id ? <CircleNotch size={18} className="animate-spin"/> : "Subscribe"}</button>}/>
      <p className="text-xs text-muted-foreground mt-4">Billed monthly, plus applicable sales tax. Switch plans or cancel any time from Manage billing; access continues to the end of the paid month.</p>
    </>}
  </AppShell>;
}

function StatusCard({ state, onManage, busy }) {
  const s = state.status;
  const line = {
    complimentary: "This account has complimentary access. No plan needed.",
    trialing: `Free trial: ${state.days_left} day${state.days_left === 1 ? "" : "s"} left (ends ${fmtDate(state.ends_at)}). Everything is unlocked.`,
    active: `${state.plan?.name || "Plan"} · $${state.plan?.amount?.toFixed(0)}/month. ${state.cancel_at_period_end ? "Cancelled, access ends" : "Renews"} ${fmtDate(state.ends_at)}.`,
    inactive: "Your trial or plan has ended. Your records stay viewable and downloadable; choose a plan to add new ones.",
  }[s];
  return <div data-testid="billing-status" className={`border-l-4 p-5 bg-white border ${s === "inactive" ? "border-l-destructive" : "border-l-accent"}`}>
    <div className="eyebrow text-muted-foreground">{s === "trialing" ? "Free trial" : s}</div>
    <div className="font-display font-bold text-lg mt-1" data-testid="billing-status-text">{line}</div>
    {state.has_subscription && s !== "complimentary" && state.fleet_role !== "driver" && <button onClick={onManage} disabled={busy} data-testid="btn-manage-billing" className="btn-dark mt-4">
      <CreditCard size={18}/> {busy ? "Opening…" : "Manage billing"}</button>}
  </div>;
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [phase, setPhase] = useState("checking");
  const tries = useRef(0);
  useEffect(() => {
    if (!sid) { setPhase("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const r = await billing.status(sid);
        if (r.payment_status === "paid") { setPhase("paid"); return; }
        if (r.status === "expired") { setPhase("error"); return; }
      } catch { /* keep polling */ }
      tries.current += 1;
      if (tries.current >= 15) { setPhase("slow"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid]);
  return <ResultShell testid={`payment-${phase}`}
    icon={phase === "paid" ? <CheckCircle size={56} weight="fill" className="text-green-600"/> : phase === "error" ? <XCircle size={56} weight="fill" className="text-destructive"/> : <CircleNotch size={56} className="animate-spin text-accent"/>}
    title={{ checking: "Confirming your payment…", paid: "You're subscribed.", slow: "Payment is still processing", error: "We couldn't confirm that payment" }[phase]}
    text={{ checking: "This takes a few seconds.", paid: "Thanks for choosing HaulGuard. Everything is unlocked.", slow: "Your bank may take a minute. Check your Plan page shortly.", error: "No charge was confirmed. You can try again from your Plan page." }[phase]}/>;
}

export function PaymentCancel() {
  return <ResultShell testid="payment-cancel" icon={<XCircle size={56} className="text-muted-foreground"/>}
    title="Checkout cancelled" text="No charge was made. You can choose a plan whenever you're ready."/>;
}

function ResultShell({ icon, title, text, testid }) {
  return <div className="min-h-screen grid-pattern flex items-center justify-center p-4">
    <div className="bg-white border border-border/60 p-8 max-w-md w-full text-center corner-bracket relative" data-testid={testid}>
      <div className="flex justify-center mb-4">{icon}</div>
      <h1 className="font-display font-black text-2xl">{title}</h1>
      <p className="text-muted-foreground mt-2">{text}</p>
      <div className="flex gap-2 justify-center mt-6">
        <Link to="/app" className="btn-amber" data-testid="btn-go-dashboard">Go to dashboard</Link>
        <Link to="/app/billing" className="btn-dark" data-testid="btn-go-billing">Plan page</Link>
      </div>
    </div>
  </div>;
}
